const STORAGE_KEY = 'onbuoy_search_location_v1';
const STORAGE_VERSION = 1;
const STORAGE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

const isBrowser = () => typeof window !== 'undefined' && !!window.sessionStorage;
const isFiniteNumber = value => Number.isFinite(value);

const isValidCoordinate = (lat, lng) => {
  return isFiniteNumber(lat) && isFiniteNumber(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

const normalizeLatLng = value => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const lat = Number(value.lat);
  const lng = Number(value.lng);
  return isValidCoordinate(lat, lng) ? { lat, lng } : null;
};

const normalizeBounds = value => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const ne = normalizeLatLng(value.ne);
  const sw = normalizeLatLng(value.sw);
  return ne && sw ? { ne, sw } : null;
};

const normalizeSource = source => {
  const validSources = ['geolocation', 'manual', 'searchBox', 'unknown'];
  return validSources.includes(source) ? source : 'unknown';
};

const removeStoredState = () => {
  if (!isBrowser()) {
    return;
  }
  window.sessionStorage.removeItem(STORAGE_KEY);
};

const normalizeLocationState = state => {
  if (!state || typeof state !== 'object') {
    return null;
  }

  const center = normalizeLatLng(state.center);
  const bounds = normalizeBounds(state.bounds);
  const updatedAt = Number(state.updatedAt);
  const source = normalizeSource(state.source);
  const address =
    typeof state.address === 'string' && state.address.trim().length > 0
      ? state.address.trim()
      : null;
  const zoom = isFiniteNumber(Number(state.zoom)) ? Number(state.zoom) : undefined;

  if (!center && !bounds) {
    return null;
  }

  const fallbackCenter = center || {
    lat: (bounds.ne.lat + bounds.sw.lat) / 2,
    lng: (bounds.ne.lng + bounds.sw.lng) / 2,
  };

  if (!isValidCoordinate(fallbackCenter.lat, fallbackCenter.lng) || !isFiniteNumber(updatedAt)) {
    return null;
  }

  return {
    center: fallbackCenter,
    bounds: bounds || null,
    source,
    updatedAt,
    ...(address ? { address } : {}),
    ...(isFiniteNumber(zoom) ? { zoom } : {}),
  };
};

export const loadSearchLocationState = () => {
  if (!isBrowser()) {
    return null;
  }

  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    if (parsed?.version !== STORAGE_VERSION) {
      removeStoredState();
      return null;
    }

    const normalizedState = normalizeLocationState(parsed?.data);
    if (!normalizedState) {
      removeStoredState();
      return null;
    }

    const expired = Date.now() - normalizedState.updatedAt > STORAGE_TTL_MS;
    if (expired) {
      removeStoredState();
      return null;
    }

    return normalizedState;
  } catch (error) {
    removeStoredState();
    return null;
  }
};

export const saveSearchLocationState = state => {
  if (!isBrowser()) {
    return;
  }

  const updatedAt = Number(state?.updatedAt) || Date.now();
  const normalizedState = normalizeLocationState({
    ...state,
    updatedAt,
  });

  if (!normalizedState) {
    return;
  }

  const payload = {
    version: STORAGE_VERSION,
    data: normalizedState,
  };

  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
};

export const clearSearchLocationState = () => {
  removeStoredState();
};

export const searchLocationStorageConfig = {
  key: STORAGE_KEY,
  version: STORAGE_VERSION,
  ttlMs: STORAGE_TTL_MS,
};
